import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  onChange: (selectedValue: string) => void;
  defaultOptionIndex?: number | undefined;
  disabled?: boolean;
  /** Render the menu in a portal so it isn't clipped by modal overflow. */
  portaledMenu?: boolean;
  /** Optional test id for the trigger (and hidden select in tests). */
  testId?: string;
  /** Optional class applied to the options popup, including when portaled. */
  menuClassName?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options = [],
  onChange,
  defaultOptionIndex,
  disabled = false,
  portaledMenu = false,
  testId,
  menuClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    options[defaultOptionIndex || 0]?.label || "Select an option"
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [prevOptions, setPrevOptions] = useState<DropdownOption[]>([]);
  const [prevDefaultIndex, setPrevDefaultIndex] = useState<number | undefined>(
    defaultOptionIndex
  );
  const [menuStyle, setMenuStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setMenuStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const handleButtonClick = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
  };

  const handleOptionClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const target = event.target as HTMLLIElement;
    setSelectedValue(target.dataset.label || "");
    onChange(target.dataset.value || "");
    setIsOpen(false);
  };

  useEffect(() => {
    if (
      JSON.stringify(options) !== JSON.stringify(prevOptions) ||
      defaultOptionIndex !== prevDefaultIndex
    ) {
      setSelectedValue(
        options[defaultOptionIndex || 0]?.label || "Select an option"
      );
      setPrevOptions(options);
      setPrevDefaultIndex(defaultOptionIndex);
    }
  }, [options, defaultOptionIndex, prevOptions, prevDefaultIndex]);

  useLayoutEffect(() => {
    if (!isOpen || !portaledMenu) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, portaledMenu, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (optionsRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const optionsList = (
    <ul
      ref={optionsRef}
      className={`customDropDown_selectOptions${
        portaledMenu ? " customDropDown_selectOptions--portaled" : ""
      }${menuClassName ? ` ${menuClassName}` : ""}`}
      style={
        portaledMenu && menuStyle && isOpen
          ? {
              display: "block",
              position: "fixed",
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
              zIndex: 1300,
            }
          : { display: isOpen ? "block" : "none" }
      }
      role="listbox"
    >
      {options.map((option) => (
        <li
          key={option.value}
          className="customDropDown_selectOptionListItem"
          data-value={option.value}
          data-label={option.label}
          onClick={handleOptionClick}
          role="option"
          aria-selected={selectedValue === option.label}
        >
          {option.label}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      ref={rootRef}
      className={`customDropDown_select${isOpen ? " open" : ""}`}
      data-testid={testId ? `${testId}-root` : undefined}
    >
      <button
        type="button"
        ref={buttonRef}
        className="customDropDown_selectButton"
        onClick={handleButtonClick}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid={testId}
      >
        <div className="customDropDown_selectButtonContentRow">
          <div className="customDropDown_selectButtonText">
            {selectedValue || "Select an option"}
          </div>
          <div className="customDropDown_selectButtonDropDownIconContainer">
            <span
              className={`fa-light fa-chevron-down customDropDown_chevron${
                isOpen ? " customDropDown_chevron--open" : ""
              }`}
              aria-hidden
            />
          </div>
        </div>
      </button>
      {portaledMenu && isOpen && menuStyle
        ? createPortal(optionsList, document.body)
        : !portaledMenu
          ? optionsList
          : null}
    </div>
  );
};

export default CustomDropdown;
